import type { SetAsideNote } from "@nova-ai/shared"
import { SET_ASIDE_NOTE_MAX_CHARS } from "@nova-ai/shared"
import { Pencil } from "lucide-react"
import { useState } from "react"

import { formatDateTime } from "../../lib/format-date"
import { useSetAsideNote } from "../../services/use-runs"
import { Button } from "../ui/button"

// The DURABLE per-product set-aside note (issue #122): keyed on the product
// (config SKU + marketplace), so it survives re-runs — distinct from the per-run
// set-aside reason shown above it. This is the first WEB mutation for set-aside.
//
// Deliberately self-contained (own file, own data hook, own state): it takes
// only { slug, configSku, note } and can be lifted out of the current run drawer
// and dropped into the NOK → "Set aside" cohort surface that issue #121
// introduces, with no change to this component.
export function SetAsideNoteEditor({
  slug,
  configSku,
  note,
}: {
  slug: string
  configSku: string
  note: SetAsideNote | null
}) {
  const mutation = useSetAsideNote(slug)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(note?.note ?? "")

  const startEditing = () => {
    setDraft(note?.note ?? "")
    setEditing(true)
  }

  const save = (body: string) => {
    mutation.mutate(
      { configSku, note: body },
      {
        onSuccess: () => setEditing(false),
      }
    )
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-muted-foreground">Set-aside note (durable)</h3>

        {!editing && (
          <Button variant="ghost" size="sm" onClick={startEditing} className="gap-1">
            <Pencil className="size-3 shrink-0" />

            {note ? "Edit" : "Add note"}
          </Button>
        )}
      </div>

      {editing ? (
        <NoteForm
          initial={note?.note ?? ""}
          draft={draft}
          onDraftChange={setDraft}
          saving={mutation.isPending}
          canClear={note !== null}
          onSave={() => save(draft.trim())}
          onClear={() => save("")}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <NoteDisplay note={note} />
      )}

      {mutation.isError && (
        <p className="text-xs text-destructive">
          {mutation.error instanceof Error ? mutation.error.message : "Could not save the note."}
        </p>
      )}
    </section>
  )
}

function NoteDisplay({ note }: { note: SetAsideNote | null }) {
  if (!note) {
    return (
      <p className="rounded-md border border-dashed border-border/60 px-3 py-2 text-sm text-muted-foreground">
        No durable note yet. A note here survives re-runs of this marketplace.
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-border/60 px-3 py-2">
      <p className="whitespace-pre-wrap text-sm text-foreground">{note.note}</p>

      <p className="text-xs text-muted-foreground">
        {`${shortAuthor(note.author)} · ${formatDateTime(note.updatedAt)}`}
      </p>
    </div>
  )
}

function NoteForm({
  initial,
  draft,
  onDraftChange,
  saving,
  canClear,
  onSave,
  onClear,
  onCancel,
}: {
  initial: string
  draft: string
  onDraftChange: (value: string) => void
  saving: boolean
  canClear: boolean
  onSave: () => void
  onClear: () => void
  onCancel: () => void
}) {
  const trimmed = draft.trim()
  // Saving an unchanged or empty draft is a no-op — disable so the operator
  // clears via the explicit Clear action rather than by blanking the field.
  const canSave = trimmed.length > 0 && trimmed !== initial.trim()
  return (
    <div className="flex flex-col gap-2">
      <textarea
        aria-label="Set-aside note"
        className="min-h-20 w-full resize-y rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        value={draft}
        maxLength={SET_ASIDE_NOTE_MAX_CHARS}
        disabled={saving}
        placeholder="Why is this product parked? e.g. Decathlon hasn't created the attributes we need — parked until they do."
        onChange={(event) => onDraftChange(event.target.value)}
      />

      <div className="flex items-center gap-2">
        <Button variant="primary" size="sm" onClick={onSave} disabled={saving || !canSave}>
          Save
        </Button>

        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>

        {canClear && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-destructive"
            onClick={onClear}
            disabled={saving}
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  )
}

// The author is the request subject id (e.g. "google:jane@gymbeam.com"); show
// the human-readable tail, matching the set-aside reason's actor rendering.
function shortAuthor(author: string): string {
  const at = author.indexOf(":")
  return at >= 0 ? author.slice(at + 1) : author
}
